/* eslint-disable no-console */
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Injectable
} from '@angular/core';
import {
  catchError,
  map, Observable,
  of,
  shareReplay
} from 'rxjs';
import { ConfigurationService } from '@sondermind/configuration';
import { camelCaseTransformer } from '@sondermind/utilities/tools';
import { StrapiContentTypes } from './constants/strapi-content-types';
import {
  StrapiAttributes,
  StrapiRes,
  StrapiResponse,
  IStrapiFooterNavigation,
  IStrapiLegalNavigation,
  IStrapiSocialNavigation,
  IStrapiGlobalText,
  IProviderMenu
} from './constants/strapi-dto';
import {
  FALLBACK_FOOTER_NAVIGATION,
  FALLBACK_LEGAL_NAVIGATION,
  FALLBACK_SOCIAL_NAVIGATION,
  FALLBACK_GLOBAL_TEXT
} from './constants/strapi-fallback-data';

@Injectable({
  providedIn: 'root'
})
export class StrapiService {
  private apiUrl = `${this.configService.env.svcBase}/core/strapi`;

  // Cache for navigation content
  private footerNavigation$: Observable<IStrapiFooterNavigation> | null = null;
  private legalNavigation$: Observable<IStrapiLegalNavigation> | null = null;
  private socialNavigation$: Observable<IStrapiSocialNavigation> | null = null;
  private globalText$: Observable<IStrapiGlobalText> | null = null;
  private providers$: Observable<IProviderMenu[]> | null = null;

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigurationService) { }

  getContent(
    contentType: StrapiContentTypes,
    filterKey: string,
    filterValues: string[],
    populate?: string
  ): Observable<StrapiAttributes[]> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('filter_key', filterKey)
      .set('page_size', '50');

    if (populate) {
      params = params.set('populate', populate);
    }

    filterValues.forEach((value) => {
      params = params.append('filter_values[]', value);
    });

    return this.httpClient.get<StrapiRes>(this.apiUrl, { params })
      .pipe(
        map((res: StrapiRes) => {
          if (res?.data?.length) {
            return res.data.map((val) => camelCaseTransformer<StrapiAttributes>(val));
          }

          return [];
        }),
        shareReplay(1)
      );
  }

  /**
   * Fetches content from Strapi.
   * Content like footer, legal, and social navigations.
   */
  getNavigationContent<T>(
    contentType: StrapiContentTypes,
    populate?: string
  ): Observable<T | null> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('page_size', '50');

    if (populate) {
      params = params.set('populate', populate);
    }

    return this.httpClient.get<StrapiResponse<T>>(this.apiUrl, { params })
      .pipe(
        map((res: StrapiResponse<T>) => {
          if (res?.data) {
            return camelCaseTransformer<T>(res.data);
          }
          return null;
        }),
        shareReplay(1)
      );
  }

  /**
   * Gets footer navigation menus from Strapi.
   * Returns fallback data on error or empty response.
   */
  getFooterNavigation$(): Observable<IStrapiFooterNavigation> {
    if (!this.footerNavigation$) {
      this.footerNavigation$ = this.getNavigationContent<IStrapiFooterNavigation>(
        StrapiContentTypes.FOOTER_NAVIGATION,
        'menus'
      ).pipe(
        map((data) => data || FALLBACK_FOOTER_NAVIGATION),
        catchError(() => of(FALLBACK_FOOTER_NAVIGATION))
      );
    }
    return this.footerNavigation$;
  }

  /**
   * Gets legal navigation links from Strapi.
   * Returns fallback data on error or empty response.
   */
  getLegalNavigation$(): Observable<IStrapiLegalNavigation> {
    if (!this.legalNavigation$) {
      this.legalNavigation$ = this.getNavigationContent<IStrapiLegalNavigation>(
        StrapiContentTypes.LEGAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_LEGAL_NAVIGATION),
        catchError(() => of(FALLBACK_LEGAL_NAVIGATION))
      );
    }
    return this.legalNavigation$;
  }

  /**
   * Gets social navigation links from Strapi.
   * Returns fallback data on error or empty response.
   */
  getSocialNavigation$(): Observable<IStrapiSocialNavigation> {
    if (!this.socialNavigation$) {
      this.socialNavigation$ = this.getNavigationContent<IStrapiSocialNavigation>(
        StrapiContentTypes.SOCIAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_SOCIAL_NAVIGATION),
        catchError(() => of(FALLBACK_SOCIAL_NAVIGATION))
      );
    }
    return this.socialNavigation$;
  }

  /**
   * Gets global text from Strapi.
   * Returns fallback data on error or empty response.
   */
  getGlobalText$(): Observable<IStrapiGlobalText> {
    if (!this.globalText$) {
      this.globalText$ = this.getNavigationContent<IStrapiGlobalText>(
        StrapiContentTypes.GLOBAL_TEXT,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_GLOBAL_TEXT),
        catchError(() => of(FALLBACK_GLOBAL_TEXT))
      );
    }
    return this.globalText$;
  }

  // Fetch providers from Strapi
  getProviders$(): Observable<IProviderMenu[]> {
    if (!this.providers$) {
      this.providers$ = this.getNavigationContent(
        StrapiContentTypes.PROVIDERS,
        '*',
      ).pipe(
        map((data) => (Array.isArray(data) ? data : []) as IProviderMenu[]),
        catchError(() => of([]))
      );
    }

    return this.providers$;
  }
}
